import type { TraceFile } from '@terra/shared-schemas-ai';
import { Box, HStack, VStack, css } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { useState } from 'react';
import { Button } from '../../../../components/button';
import { Collapsible } from '../../../../components/collapsible';
import { Text } from '../../../../components/styled/text';
import { MarkdownPreview } from '../../../markdown-preview';
import { getTraceFileLanguage, getTraceFileType } from '../utils/toolsUtils';
import { parseCodeResult, formatContentForDisplay } from '../utils/trace-files';
import { ReadOnlyCodeEditor } from '../../components/ReadOnlyCodeEditor';

type TraceFileBlockProps = {
  file: TraceFile;
  label?: string;
  hasError?: boolean;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /**
   * Rendering mode for the file content.
   * - 'auto': keep normal markdown rendering (default)
   * - 'codeBlock': force code-block formatting (monospace), even for prose
   */
  renderMode?: 'auto' | 'codeBlock';
  /**
   * Base font size for MarkdownPreview.
   * Note: MarkdownPreview renders code blocks at `fontSize - 2`.
   */
  contentFontSize?: number;
};
const traceFileBlockContentClassName = css({
  // Keep TraceFileBlock's header (`px={3}`) and the rendered code content aligned.
  '& .wmde-markdown': {
    padding: 0,
    margin: 0,
  },
  '& .wmde-markdown pre': {
    padding: 0,
    margin: 0,
  },
});

export function TraceFileBlock({
  file,
  label,
  hasError,
  collapsible = false,
  defaultOpen = true,
  renderMode = 'auto',
  contentFontSize = 12,
}: TraceFileBlockProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const isResultFile = file.name.includes('code_result_');
  const fileType = getTraceFileType(file.name, file.content);
  const showMoreLabel = 'Show more';
  let displayContent = file.content;

  if (isResultFile) {
    displayContent = parseCodeResult(file.content);
  }

  const codeLanguage = getTraceFileLanguage(displayContent, fileType);
  const formattedContent = formatContentForDisplay(displayContent, fileType);

  if (collapsible) {
    return (
      <VStack alignItems="start" gap={1} width="100%">
        <Collapsible.Root
          open={isOpen}
          onOpenChange={({ open }) => setIsOpen(open)}
        >
          <Collapsible.Trigger asChild>
            <Button variant="ghost" size="xs">
              <HStack gap={1} alignItems="center">
                <Box display="flex" alignItems="center" color="fg.muted">
                  {isOpen ? (
                    <ArrowDown01Icon size={14} />
                  ) : (
                    <ArrowRight01Icon size={14} />
                  )}
                </Box>
                {label ? (
                  <Text fontSize="xs" fontWeight="normal" color="fg.muted">
                    {label}
                  </Text>
                ) : (
                  <Text fontSize="xs" fontWeight="normal" color="fg.muted">
                    {showMoreLabel}
                  </Text>
                )}
              </HStack>
            </Button>
          </Collapsible.Trigger>

          <Collapsible.Content>
            <Box
              bg="bg.canvas"
              p={0}
              borderRadius="sm"
              borderWidth="1px"
              borderColor={hasError ? 'border.error' : 'border.subtle'}
              width="100%"
              maxH="300px"
              overflow="hidden"
              mt={1}
            >
              {label && (
                <Box
                  px={3}
                  py={2}
                  bg="bg.muted"
                  borderBottomWidth="1px"
                  borderColor="border.default"
                >
                  <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
                    {label}
                  </Text>
                </Box>
              )}
              <Box
                px={3}
                py={2}
                overflow="auto"
                maxH="300px"
                className={traceFileBlockContentClassName}
              >
                {renderMode === 'codeBlock' ? (
                  <ReadOnlyCodeEditor
                    value={displayContent}
                    maxHeight="300px"
                    minHeight="44px"
                    language={
                      codeLanguage === 'python' ||
                      codeLanguage === 'javascript' ||
                      codeLanguage === 'json'
                        ? codeLanguage
                        : 'text'
                    }
                  />
                ) : (
                  <MarkdownPreview
                    source={formattedContent}
                    fontSize={contentFontSize}
                    compact
                  />
                )}
              </Box>
            </Box>
          </Collapsible.Content>
        </Collapsible.Root>
      </VStack>
    );
  }

  return (
    <VStack alignItems="start" gap={1} width="100%">
      <Box
        bg="bg.canvas"
        p={0}
        borderRadius="sm"
        borderWidth="1px"
        borderColor={hasError ? 'border.error' : 'border.subtle'}
        width="100%"
        maxH="300px"
        overflow="hidden"
      >
        {label && (
          <Box
            px={3}
            py={2}
            bg="bg.muted"
            borderBottomWidth="1px"
            borderColor="border.default"
          >
            <Text fontSize="2xs" fontWeight="medium" color="fg.muted">
              {label}
            </Text>
          </Box>
        )}
        <Box
          px={3}
          py={2}
          overflow="auto"
          maxH="300px"
          className={traceFileBlockContentClassName}
        >
          {renderMode === 'codeBlock' ? (
            <ReadOnlyCodeEditor
              value={displayContent}
              maxHeight="300px"
              minHeight="44px"
              language={
                codeLanguage === 'python' ||
                codeLanguage === 'javascript' ||
                codeLanguage === 'json'
                  ? codeLanguage
                  : 'text'
              }
            />
          ) : (
            <MarkdownPreview
              source={formattedContent}
              fontSize={contentFontSize}
              compact
            />
          )}
        </Box>
      </Box>
    </VStack>
  );
}
